import { LogLevel } from '../types/analytics';

interface LogEntry {
  timestamp: number;
  service: string;
  level: LogLevel;
  message: string;
  data?: unknown;
}

interface AnalyticsDebug {
  log: (service: string, data: unknown) => void;
  enabled: boolean;
  timestamp: number;
  level: LogLevel;
}

declare global {
  interface Window {
    analyticsDebug?: AnalyticsDebug;
  }
}

class AnalyticsLogger {
  private static instance: AnalyticsLogger;
  private logs: LogEntry[] = [];
  private isDebugMode: boolean;
  private maxLogs: number = 1000;

  private constructor() {
    // Intentar obtener el modo de depuración de diferentes fuentes
    this.isDebugMode = this.determineDebugMode();
  }

  private determineDebugMode(): boolean {
    try {
      // Verificar el entorno de pruebas
      if (process.env.NODE_ENV === 'test') {
        return true;
      }

      // Verificar variables de Vite si están disponibles
      if (typeof import.meta !== 'undefined' && import.meta.env) {
        return import.meta.env.DEV || import.meta.env.VITE_DEBUG_ANALYTICS === 'true';
      }

      // Verificar localStorage como fallback
      if (typeof window !== 'undefined' && window.localStorage) {
        const debugSetting = window.localStorage.getItem('analytics_debug_mode');
        if (debugSetting !== null) {
          return debugSetting === 'true';
        }
      }

      // Valor por defecto
      return false;
    } catch {
      // En caso de error, retornar false por seguridad
      return false;
    }
  }

  public static getInstance(): AnalyticsLogger {
    if (!AnalyticsLogger.instance) {
      AnalyticsLogger.instance = new AnalyticsLogger();
    }
    return AnalyticsLogger.instance;
  }

  private createLogEntry(
    service: string,
    level: LogLevel,
    message: string,
    data?: unknown
  ): LogEntry {
    return {
      timestamp: Date.now(),
      service,
      level,
      message,
      data,
    };
  }

  private addLog(entry: LogEntry): void {
    try {
      this.logs.push(entry);

      // Mantener un límite de logs estricto
      if (this.logs.length > this.maxLogs) {
        this.logs.shift(); // Elimina el log más antiguo para mantener el límite
      }

      // Actualizar el objeto analyticsDebug en window
      if (typeof window !== 'undefined') {
        window.analyticsDebug = {
          log: (service: string, data: unknown) => {
            this.info(service, 'Debug log', data);
          },
          enabled: this.isDebugMode,
          timestamp: entry.timestamp,
          level: entry.level,
        };
      }

      // Mostrar en consola si está en modo debug
      if (this.isDebugMode) {
        this.writeToConsole(entry);
      }
    } catch (error) {
      // Evitar que los errores de logging interrumpan la aplicación
      console.error('Error in analytics logging:', error);
    }
  }

  private writeToConsole(entry: LogEntry): void {
    try {
      const consoleMethod =
        {
          info: console.log,
          warning: console.warn,
          error: console.error,
        }[entry.level] || console.log;

      const timestamp = new Date(entry.timestamp).toISOString();
      const message = `[${timestamp}] [${entry.service}] ${entry.message}`;

      if (entry.data) {
        consoleMethod(message, entry.data);
      } else {
        consoleMethod(message);
      }
    } catch {
      // Silenciar errores de consola
    }
  }

  public log(service: string, message: string, data?: unknown, level: LogLevel = 'info'): void {
    if (!service || !message) {
      console.warn('Invalid log parameters:', { service, message });
      return;
    }

    const entry = this.createLogEntry(service, level, message, data);
    this.addLog(entry);
  }

  public info(service: string, message: string, data?: unknown): void {
    this.log(service, message, data, 'info');
  }

  public warn(service: string, message: string, data?: unknown): void {
    this.log(service, message, data, 'warning');
  }

  public error(service: string, message: string, data?: unknown): void {
    this.log(service, message, data, 'error');

    // Si Sentry está disponible, enviar el error
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureMessage(`[${service}] ${message}`, {
        level: 'error',
        extra: { data },
      });
    }
  }

  public getLogs(
    options: {
      service?: string;
      level?: LogLevel;
      startTime?: number;
      endTime?: number;
    } = {}
  ): LogEntry[] {
    let filteredLogs = [...this.logs];

    if (options.service) {
      filteredLogs = filteredLogs.filter(log => log.service === options.service);
    }

    if (options.level) {
      filteredLogs = filteredLogs.filter(log => log.level === options.level);
    }

    if (options.startTime) {
      filteredLogs = filteredLogs.filter(log => log.timestamp >= options.startTime!);
    }

    if (options.endTime) {
      filteredLogs = filteredLogs.filter(log => log.timestamp <= options.endTime!);
    }

    return filteredLogs;
  }

  public clearLogs(): void {
    this.logs = [];
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem('analytics_debug_mode');
    }
  }

  public setDebugMode(enabled: boolean): void {
    this.isDebugMode = enabled;
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('analytics_debug_mode', String(enabled));
    }
  }

  public getDebugMode(): boolean {
    return this.isDebugMode;
  }
}

export const analyticsLogger = AnalyticsLogger.getInstance();
export default analyticsLogger;
